import {
  useFindAllCompaniesQuery,
  useFindAlldepartmentsQuery,
  useFindAllInventoryHardwareQuery,
  useFindOneHardwareQuery,
  useGetAllTypesQuery,
  useRemoveHardwareMutation,
} from '../../api/api-hooks';
import { HardwareItemFilter, HardwareItemForm } from './hardwareTypes';
import { Grid, TextField, Typography } from '@mui/material';
import { Autocomplete } from '@mui/material';
import { nullStringCheck } from '../../utils/stringManipulation';
import { Item } from '../../utils/sharedTypes';
import {
  getISOStringOneYearFromToday,
  truncateISOString,
} from '../../utils/dateTime';
import useHardwareItemUpdater from './useHardwareItemUpdater';
import {
  hardwareHandlerMiddleware,
  hardwareTransferValidator,
} from './hardwareUtils';
import {
  Company,
  Department,
  InventoryHardware,
  Order,
} from '../../api/api-generated';
import React, { useEffect, useReducer } from 'react';
import HardwareTable from './HardwareTable';
import ItemOptionsBarWithLinking from '../../components/ItemOptionsBarWithLinking';
import AddItemDialog from '../../components/AddItemDialog';
import HardwareFilterBar from './HardwareFilterBar';
import useHardwareItemCreator from './useHardwareItemCreator';
import DepartmentsButton from '../departments/DepartmentsButton';

interface Props {
  selectedHardware: Item[];
  setSelectedHardware: React.Dispatch<React.SetStateAction<Item[]>>;
  order: Order;
}
const initialFilters: HardwareItemFilter = {
  hardwareType: null,
  company: null,
  serial: '',
  soldDateFrom: '',
  soldDateTo: '',
  warrantyActive: '',
};
const addFormInitialState: HardwareItemForm = {
  hardwareType: null,
  companies: [],
  firmware: undefined,
  notes: undefined,
  shipper: undefined,
  serial: '',
  warrantyStartDate: truncateISOString(new Date().toISOString()),
  warrantyEndDate: truncateISOString(getISOStringOneYearFromToday()),
  trackingNumber: undefined,
  order: null,
  lptSold: 'true',
  manufacturer: 'LP Technologies',
  inventoryHardware: null,
  department: null,
};

const HardwareOptionsBarForOrder = ({
  selectedHardware,
  setSelectedHardware,
  order,
}: Props) => {
  const [deleteHardware] = useRemoveHardwareMutation();
  const hardwareCreator = useHardwareItemCreator();
  const hardwareUpdater = useHardwareItemUpdater();
  const { data: companies } = useFindAllCompaniesQuery({});
  const { data: inventoryHardware } = useFindAllInventoryHardwareQuery({});
  const { data: hardwareTypes } = useGetAllTypesQuery({});
  const { data: departments } = useFindAlldepartmentsQuery({});
  useEffect(() => {
    addFormInitialState.order = order;
    addFormInitialState.companies = [order.company];
  }, [order]);
  const [filters, setFilters] = useReducer(
    (curVals: HardwareItemFilter, newVals: any) => ({
      ...curVals,
      ...newVals,
    }),
    initialFilters
  );

  if (
    inventoryHardware === undefined ||
    companies === undefined ||
    hardwareTypes === undefined ||
    departments === undefined
  )
    return null;
  return (
    <>
      <ItemOptionsBarWithLinking
        selectedItems={selectedHardware}
        setSelectedItems={setSelectedHardware}
        handlerMiddleware={hardwareHandlerMiddleware}
        itemValidator={hardwareTransferValidator}
        formInstructions='Inventory Hardware and Firmware are required.'
        addFormInitialState={addFormInitialState}
        itemName='Hardware Sold'
        itemToLink={order}
        itemToLinkName='order'
        itemToLinkIdName='orderId'
        itemToLinkTitle='Order'
        tableCreator={(setHardwareToLink) => (
          <>
            <HardwareFilterBar
              filters={filters}
              setFilters={setFilters}
              initialFilters={initialFilters}
              hideCompany
            />
            <HardwareTable
              setSelectedHardware={setHardwareToLink}
              lptSold={true}
              order={order}
              excludeOrder={true}
              filters={filters}
            />
          </>
        )}
        addDialogCreator={(
          addModalOpen: boolean,
          setAddModalOpen: React.Dispatch<React.SetStateAction<boolean>>,
          childrenCreator: Function
        ) => (
          <AddItemDialog
            addModalOpen={addModalOpen}
            setAddModalOpen={setAddModalOpen}
            addFormInitialState={addFormInitialState}
            itemCreator={hardwareCreator}
            itemValidator={hardwareTransferValidator}
            formInstructions='Inventory Hardware and Firmware are required.'
            handlerMiddleware={hardwareHandlerMiddleware}
            itemName='Hardware Sold'
            children={(addFormState: HardwareItemForm, handler: any) => (
              <Grid container direction='column' spacing={1}>
                <Grid item>
                  <Autocomplete
                    onChange={(event, value: null | InventoryHardware) => {
                      handler(event, value?.hardwareType, 'hardwareType');
                      handler(event, value, 'inventoryHardware');
                    }}
                    value={addFormState.inventoryHardware}
                    options={inventoryHardware}
                    getOptionLabel={(inventory) => inventory.serial}
                    style={{ width: 300, marginTop: '.5rem' }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label='Select Inventory Hardware'
                        variant='outlined'
                      />
                    )}
                  />
                </Grid>
                <Grid item container alignItems='center' spacing={1}>
                  <Grid item>
                    <Autocomplete
                      onChange={(event, value) => {
                        handler(event, value, 'department');
                      }}
                      value={addFormState.department}
                      options={departments.filter((department: Department) =>
                        addFormState.companies.some(
                          (company) => company.id === department?.companyId
                        )
                      )}
                      getOptionLabel={(department) => department.departmentName}
                      style={{ width: 300 }}
                      disabled={
                        !addFormState.companies ||
                        addFormState.companies.length < 1
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label='Select Department'
                          variant='outlined'
                        />
                      )}
                    />
                  </Grid>
                  <Grid item>
                    <DepartmentsButton company={addFormState.companies[0]} />
                  </Grid>
                </Grid>
                <Grid item>
                  <TextField
                    margin='dense'
                    name='manufacturer'
                    label='Manufacturer'
                    fullWidth
                    value={nullStringCheck(addFormState.manufacturer)}
                    onChange={handler}
                  />
                </Grid>
                <Grid item>
                  <TextField
                    margin='dense'
                    name='firmware'
                    label='Firmware'
                    fullWidth
                    value={nullStringCheck(addFormState.firmware)}
                    onChange={handler}
                  />
                </Grid>
                <Grid item>
                  <Grid container alignItems='center' spacing={1}>
                    <Grid item>
                      <Typography>Warranty Start: </Typography>
                    </Grid>
                    <Grid item>
                      <TextField
                        type='date'
                        margin='dense'
                        name='warrantyStartDate'
                        value={truncateISOString(
                          nullStringCheck(addFormState.warrantyStartDate)
                        )}
                        onChange={handler}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item>
                  <Grid container alignItems='center' spacing={1}>
                    <Grid item>
                      <Typography>Warranty End: </Typography>
                    </Grid>
                    <Grid item>
                      <TextField
                        type='date'
                        margin='dense'
                        name='warrantyEndDate'
                        value={truncateISOString(
                          nullStringCheck(addFormState.warrantyEndDate)
                        )}
                        onChange={handler}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item>
                  <TextField
                    margin='dense'
                    name='trackingNumber'
                    label='Tracking #'
                    fullWidth
                    value={nullStringCheck(addFormState.trackingNumber)}
                    onChange={handler}
                  />
                </Grid>
                <Grid item>
                  <TextField
                    margin='dense'
                    name='shipper'
                    label='Shipper'
                    fullWidth
                    value={nullStringCheck(addFormState.shipper)}
                    onChange={handler}
                  />
                </Grid>
              </Grid>
            )}
          />
        )}
        childrenCreator={(addFormState: HardwareItemForm, handler: any) => (
          <Grid container spacing={3}>
            <Grid container item xs={6} direction='column' spacing={1}>
              <Grid item>
                <TextField
                  margin='dense'
                  name='serial'
                  label='Serial #'
                  fullWidth
                  value={nullStringCheck(addFormState.serial)}
                  onChange={handler}
                />
              </Grid>
              <Grid item>
                <Autocomplete
                  onChange={(event, value) => {
                    handler(event, value, 'hardwareType');
                  }}
                  value={addFormState.hardwareType}
                  options={hardwareTypes}
                  getOptionLabel={(type) => type.typeName}
                  style={{ width: 300 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Select Type'
                      variant='outlined'
                    />
                  )}
                />
              </Grid>
              {addFormState.companies.length > 0 ? (
                addFormState.companies.map((company, idx) => (
                  <Grid item>
                    <Autocomplete
                      onChange={(event, value) => {
                        handler(event, value, `companies${idx}`);
                      }}
                      value={company}
                      options={companies}
                      getOptionLabel={(company) => company.companyName}
                      style={{ width: 300 }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label='Select Company'
                          variant='outlined'
                        />
                      )}
                    />
                  </Grid>
                ))
              ) : (
                <Grid item>
                  <Autocomplete
                    onChange={(event, value) => {
                      handler(event, value, 'companies0');
                    }}
                    value={addFormState.companies[0]}
                    options={companies}
                    getOptionLabel={(company) => company.companyName}
                    style={{ width: 300 }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label='Select Company'
                        variant='outlined'
                      />
                    )}
                  />
                </Grid>
              )}
              <Grid item>
                <Autocomplete
                  onChange={(event, value) => {
                    handler(event, value, 'department');
                  }}
                  value={addFormState.department}
                  options={departments.filter((department: Department) =>
                    addFormState.companies.some(
                      (company: Company) => company.id === department?.companyId
                    )
                  )}
                  getOptionLabel={(department) => department.departmentName}
                  style={{ width: 300 }}
                  disabled={
                    !addFormState.companies || addFormState.companies.length < 1
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Select Department'
                      variant='outlined'
                    />
                  )}
                />
              </Grid>

              <Grid item>
                <TextField
                  margin='dense'
                  name='firmware'
                  label='Firmware'
                  fullWidth
                  value={nullStringCheck(addFormState.firmware)}
                  onChange={handler}
                />
              </Grid>
              <Grid item>
                <TextField
                  margin='dense'
                  name='manufacturer'
                  label='Manufacturer'
                  fullWidth
                  value={nullStringCheck(addFormState.manufacturer)}
                  onChange={handler}
                />
              </Grid>
            </Grid>
            <Grid container item xs={6} direction='column' spacing={1}>
              <Grid item>
                <Grid container alignItems='center' spacing={1}>
                  <Grid item>
                    <Typography>Warranty Start: </Typography>
                  </Grid>
                  <Grid item>
                    <TextField
                      type='date'
                      margin='dense'
                      name='warrantyStartDate'
                      value={truncateISOString(
                        nullStringCheck(addFormState.warrantyStartDate)
                      )}
                      onChange={handler}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid container alignItems='center' spacing={1}>
                  <Grid item>
                    <Typography>Warranty End: </Typography>
                  </Grid>
                  <Grid item>
                    <TextField
                      type='date'
                      margin='dense'
                      name='warrantyEndDate'
                      value={truncateISOString(
                        nullStringCheck(addFormState.warrantyEndDate)
                      )}
                      onChange={handler}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <TextField
                  margin='dense'
                  name='trackingNumber'
                  label='Tracking #'
                  fullWidth
                  value={nullStringCheck(addFormState.trackingNumber)}
                  onChange={handler}
                />
              </Grid>
              <Grid item>
                <TextField
                  margin='dense'
                  name='shipper'
                  label='Shipper'
                  fullWidth
                  value={nullStringCheck(addFormState.shipper)}
                  onChange={handler}
                />
              </Grid>
            </Grid>
          </Grid>
        )}
        itemCreator={hardwareCreator}
        itemGetter={useFindOneHardwareQuery}
        itemUpdater={hardwareUpdater}
        itemDeleter={deleteHardware}
      />
    </>
  );
};

export default HardwareOptionsBarForOrder;
